import { type FileRecordListScope, useMoveFileRecord } from "@/api/fileRecords";
import { useWorkspaces } from "@/api/workspaces";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { moveRecordCascading } from "@/pages/workspaces/utils/folderMove";
import type { FileRecord } from "@cohesive/api-client";
import { Layers, User } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

interface MoveFileRecordDialogProps {
  record: FileRecord | null;
  onClose: () => void;
  /** Called when move succeeds, before `onClose`. */
  onSuccess?: () => void;
  /** The workspace the file currently lives in (workspace-class only). */
  workspaceId: string;
  /** All records of the source scope; folder moves cascade over these. */
  allRecords: FileRecord[];
}

function getLeafName(path: string): string {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segments = cleaned.split("/").filter(Boolean);
  return segments[segments.length - 1] || cleaned;
}

function getMoveSource(
  record: FileRecord | null,
  workspaceId: string,
): FileRecordListScope | undefined {
  if (!record) return undefined;
  if (record.class === "workspace") {
    return {
      class: "workspace",
      workspace_id: record.workspace_id ?? workspaceId,
    };
  }
  if (record.class === "canvas" && record.canvas_id) {
    return { class: "canvas", canvas_id: record.canvas_id };
  }
  if (record.class === "flow-session" && record.flow_session_id) {
    return { class: "flow-session", flow_session_id: record.flow_session_id };
  }
  return undefined;
}

export function MoveFileRecordDialog({
  record,
  onClose,
  onSuccess,
  workspaceId,
  allRecords,
}: MoveFileRecordDialogProps) {
  const [targetId, setTargetId] = useState<string>("");
  const [isMoving, setIsMoving] = useState(false);
  const isWorkspaceSource = record?.class === "workspace";
  const { data: workspaces = [] } = useWorkspaces();
  const moveFileRecord = useMoveFileRecord(getMoveSource(record, workspaceId));

  // Reset when a new record is opened.
  useEffect(() => {
    if (record) {
      setTargetId("");
      setIsMoving(false);
    }
  }, [record?.id]);

  // Sort: team workspaces first, private last, alpha within each group.
  const sortedWorkspaces = [...workspaces].sort((a, b) => {
    if (a.is_private === b.is_private) return a.name.localeCompare(b.name);
    return a.is_private ? 1 : -1;
  });

  // Workspace files can't move to their current workspace; canvas /
  // flow-session files can target any workspace.
  const eligibleWorkspaces = isWorkspaceSource
    ? sortedWorkspaces.filter((ws) => ws.id !== workspaceId)
    : sortedWorkspaces;

  // isMoving (not the mutation's isPending) gates the button: folder moves
  // are a cascade of many sequential mutations and isPending flickers
  // between them.
  const isBusy = isMoving;

  const handleMove = async () => {
    if (!record || !targetId || isBusy) return;

    const isFolder = record.path.endsWith("/");
    const leafName = getLeafName(record.path);
    // Drop the record at the root of the target workspace, preserving its name.
    const destinationPath = isFolder ? `${leafName}/` : leafName;

    setIsMoving(true);
    try {
      // Folder moves must cascade: the backend move primitive only updates
      // the row it targets, so moving just the marker would orphan every
      // nested file/subfolder at its old path in the source workspace.
      await moveRecordCascading({
        record,
        payload: {
          destination: {
            class: "workspace",
            workspace_id: targetId,
            path: destinationPath,
            is_folder: isFolder,
          },
        },
        allRecords,
        moveOne: (vars) => moveFileRecord.mutateAsync(vars),
      });
      toast.success("Moved successfully");
      onSuccess?.();
      onClose();
    } catch {
      toast.error("Failed to move file");
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <Dialog
      open={record !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move to workspace</DialogTitle>
          <DialogDescription>
            Choose a workspace to move
            {record ? ` "${getLeafName(record.path)}"` : " this file"} to.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select a workspace…" />
            </SelectTrigger>
            <SelectContent>
              {eligibleWorkspaces.length === 0 ? (
                <div className="px-3 py-6 text-sm text-muted-foreground text-center">
                  {isWorkspaceSource
                    ? "No other workspaces available."
                    : "No workspaces available."}
                </div>
              ) : (
                eligibleWorkspaces.map((ws) => {
                  const Icon = ws.is_private ? User : Layers;
                  const label = ws.is_private ? "My Workspace" : ws.name;
                  return (
                    <SelectItem key={ws.id} value={ws.id} className="py-2.5">
                      <span className="flex items-center gap-2.5">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-foreground/[0.06] dark:bg-n-95/[0.06]">
                          <Icon className="h-3.5 w-3.5 text-foreground/60" />
                        </span>
                        <span className="text-[13px] font-medium truncate">
                          {label}
                        </span>
                      </span>
                    </SelectItem>
                  );
                })
              )}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={!targetId || isBusy}>
            {isBusy ? "Moving…" : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
